import React, { createContext, useContext, useState, useEffect } from "react";
import baseURL from "../components/authComponents/baseURL/BaseURL";

const SiteSettingsContext = createContext();

export const useSiteSettings = () => useContext(SiteSettingsContext);

// Default fallback — used while the API call is in-flight or if it fails
const DEFAULTS = {
  // The team's WhatsApp number is deliberately NOT shipped to the browser any
  // more — every "message us" link goes through the API redirect below, so the
  // number never appears in the page source for scrapers.
  whatsapp_redirect_path: "/support_chat/whatsapp",
  messenger_redirect_path: "/support_chat/messenger",
  support_widget: {
    whatsapp_enabled: true,
    call_number: "+923143443051",
    messenger_enabled: false,
    chat_enabled: true,
    response_time: "30 minutes – 3 hours",
    team_online: false,
  },
};

// Cache key so we don't flash a missing number on repeat visits
const CACHE_KEY = "sp_site_settings";

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
};

export const SiteSettingsProvider = ({ children }) => {
  const cached = readCache();
  const [settings, setSettings] = useState(cached || DEFAULTS);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await baseURL.get("/site_settings/public");
        const data = res.data?.data || {};
        const merged = { ...DEFAULTS, ...data };
        setSettings(merged);
        localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
      } catch (err) {
        console.log("Failed to fetch site settings:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Helper to build a WhatsApp chat link. Points at the API redirect endpoint,
  // which knows the number server-side and 302s the visitor to wa.me.
  const whatsappLink = (customMessage) => {
    const msg = encodeURIComponent(
      customMessage ||
        "Hello! I am interested in registering my profile on Select Proposal"
    );
    // Same-origin proxy path in production (vercel.json rewrites /go/whatsapp ->
    // the API endpoint) so visitors never see the raw herokuapp.com host on
    // hover. Falls back to the API host when not served from the web domain.
    const path = settings.whatsapp_redirect_path || "/support_chat/whatsapp";
    const sameOrigin =
      typeof window !== "undefined" &&
      /(^|\.)selectproposal\.com$/.test(window.location.hostname);
    if (sameOrigin) return `/go/whatsapp?text=${msg}`;
    return `${baseURL.defaults.baseURL}${path}?text=${msg}`;
  };

  // Messenger link — also a server-side redirect, so the Page handle can be
  // changed from Admin -> Settings without a redeploy.
  // ── Contact channel ──────────────────────────────────────────────────
  // When an admin switches the WhatsApp button off (Admin -> Settings ->
  // Support chat) every "Message us on WhatsApp" CTA becomes a direct phone
  // call instead of a dead link that 403s at the redirect endpoint.
  const whatsappEnabled = settings.support_widget?.whatsapp_enabled !== false;

  const callNumber =
    settings.support_widget?.call_number ||
    settings.support_call_number ||
    DEFAULTS.support_call_number;

  // Independent of WhatsApp: admins choose 'whatsapp_off' (default), 'always'
  // or 'never' in Admin -> Settings -> Support chat.
  const callButtonMode = settings.support_widget?.call_button_mode || "whatsapp_off";
  const callEnabled =
    settings.support_widget?.call_enabled !== undefined
      ? settings.support_widget.call_enabled
      : callButtonMode === "always"
      ? true
      : callButtonMode === "never"
      ? false
      : !whatsappEnabled;

  const callLink = () => `tel:${String(callNumber).replace(/[^0-9+]/g, "")}`;

  // One object every CTA can render from, so the copy stays consistent.
  const contactChannel = (customMessage) =>
    whatsappEnabled
      ? {
          mode: "whatsapp",
          href: whatsappLink(customMessage),
          external: true,
          labelEn: "Message us",
          labelUr: "WhatsApp پر رابطہ کریں",
          shortEn: "WhatsApp Us",
          titleEn: "Talk to Us on WhatsApp",
        }
      : {
          mode: "call",
          href: callLink(),
          external: false,
          labelEn: `Call us directly — ${callNumber}`,
          labelUr: "براہِ راست کال کریں",
          shortEn: "Call Us Directly",
          titleEn: "Call Us Directly",
        };

  const messengerLink = () => {
    const sameOrigin =
      typeof window !== "undefined" &&
      /(^|\.)selectproposal\.com$/.test(window.location.hostname);
    if (sameOrigin) return "/go/messenger";
    return `${baseURL.defaults.baseURL}${
      settings.messenger_redirect_path || "/support_chat/messenger"
    }`;
  };

  return (
    <SiteSettingsContext.Provider
      value={{
        settings,
        loading,
        whatsappLink,
        messengerLink,
        whatsappEnabled,
        callNumber,
        callLink,
        callEnabled,
        callButtonMode,
        contactChannel,
        supportWidget: settings.support_widget || DEFAULTS.support_widget,
      }}
    >
      {children}
    </SiteSettingsContext.Provider>
  );
};

export default SiteSettingsContext;
